import React, { useContext, createContext, useEffect, useState } from "react";
import { initializeApp, getApps } from "firebase/app";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { connectStorageEmulator, getStorage } from "firebase/storage";
import { getSupabase } from "../data/client";

// Check if running on localhost (emulator mode)
const isLocalhost = typeof window !== "undefined" &&
  window.location.hostname === "localhost";

// Development project ID - always use this for emulators regardless of which
// Firebase project is currently selected in the CLI. This ensures the React app
// and emulator are always using the same project context.
const DEV_PROJECT_ID = "dive-dev-f9d3f";

// When running locally, always use dev project config to match emulator context.
// This prevents CORS and permission errors when switching between firebase projects
// (e.g., `firebase use qa` or `firebase use production`) while developing locally.
const configDirect = isLocalhost
  ? {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: `${DEV_PROJECT_ID}.firebaseapp.com`,
      projectId: DEV_PROJECT_ID,
      storageBucket: `${DEV_PROJECT_ID}.appspot.com`,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
      measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
    }
  : {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
      measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
    };

const authContext = createContext();

// Global variable to hold the Firebase app instance
let firebaseAppInstance;
let isEmulatorConnected = false;
let db, auth, functions, storage;

// Idempotent [DEFAULT] app init. Exported for the magic-link landing page
// (AuthMagicPage), which must sign in on the SAME app instance the shell and
// route guards read — Firebase Auth persists sessions keyed by appName, so a
// session created on a named secondary app is invisible to the main app.
export const ensureFirebaseApp = () => {
  if (!firebaseAppInstance) {
    firebaseAppInstance =
      getApps().find((a) => a.name === "[DEFAULT]") ??
      initializeApp(configDirect);
  }
  return firebaseAppInstance;
};

//Wraps the app and makes auth object available to any child calling useAuth()
export const ProvideAuth = ({ children }) => {
  const authHook = useProvideAuth();
  return (
    <authContext.Provider value={authHook}>{children}</authContext.Provider>
  );
};

//Hook for child compoenets to get the auth object
//and re-render upon change.
export const useAuth = () => {
  return useContext(authContext);
};

//Provider hook that creates auth object and handles state
const useProvideAuth = () => {
  const [isLoadingFirebase, setIsLoadingFirebase] = useState(true);
  console.log("Triggering ProvideAuth Hook to run");

  useEffect(() => {
    if (!firebaseAppInstance) {
      console.log("Initializing Firebase App with project:", configDirect.projectId);
      if (isLocalhost) {
        console.log(
          `🏠 Local development mode: Using dev project (${DEV_PROJECT_ID}) for emulator compatibility`
        );
      }
    }
    ensureFirebaseApp();
    db = getFirestore(firebaseAppInstance);
    auth = getAuth(firebaseAppInstance);
    functions = getFunctions(firebaseAppInstance);
    storage = getStorage(firebaseAppInstance);

    // LOCAL DEVELOPMENT — the Firebase emulator suite is being retired (N1/R6).
    //
    // The Auth emulator is gone unconditionally and cannot come back: Supabase
    // verifies Firebase ID tokens against Google's JWKS for the registered
    // project, and emulator-issued tokens are not signed with that project's
    // keys. There is no vendor path around it. Local development therefore signs
    // in against the REAL dev Firebase Auth project, which is the config
    // localhost already forces (DEV_PROJECT_ID above).
    //
    // The other three default to OFF, so `npm start` now talks to real dev
    // Firestore/Functions/Storage alongside a LOCAL Supabase stack — which is
    // the point: BigQuery was never available in the emulators, so reporting
    // could never be exercised locally. Postgres can be.
    //
    // WRITE SAFETY, STATED PLAINLY: with the emulators off, local writes to
    // Firestore hit the real dev project. Set VITE_USE_FIREBASE_EMULATORS=true
    // to sandbox the legacy (not-yet-ported) surfaces while you work on them.
    // That escape hatch disappears with the last ported domain at N12.
    const useFirebaseEmulators =
      isLocalhost && import.meta.env.VITE_USE_FIREBASE_EMULATORS === "true";

    if (useFirebaseEmulators && !isEmulatorConnected) {
      try {
        connectFirestoreEmulator(db, "localhost", 8080);
        connectFunctionsEmulator(functions, "localhost", 5001);
        connectStorageEmulator(storage, "localhost", 9199);
        isEmulatorConnected = true;
      } catch (error) {
        console.warn("Emulator connection error (may already be connected):", error);
        isEmulatorConnected = true;
      }
    }
    setIsLoadingFirebase(false);
  }, []);

  return {
    firebaseAppInstance,
    auth,
    // The Supabase client. On `next` this is the data path; db/functions/storage
    // are the legacy Firebase handles, kept until the last domain ports at N12.
    // CI ratchets their call-site count down from the recorded baseline — see
    // docs/phaseN-ported-domains.json.
    supabase: getSupabase(),
    db,
    functions,
    storage,
    isLoadingFirebase,
  };
};
